import { getLocaleString } from '@assets/i18n/config';
import type { AssetClassLevel } from '@generated/graphql';
import {
  ASSET_CLASS_FIELD_BY_LEVEL,
  type AssetClass,
  type FinancialProduct,
} from '@interfaces/financialProduct';

/** Bucket key for products missing the asset class at the selected level. */
export const UNCATEGORIZED_ASSET_CLASS_GROUP_KEY = '__uncategorized__';

export type FinancialProductAssetClassGroup = {
  groupKey: string;
  headerLabel: string;
  products: FinancialProduct[];
};

/**
 * Groups financial products by the asset class field for the given level
 * (same mapping as {@link useAssetClassGrouping}). Used by the asset-class
 * security dropdown; callers should only invoke when strategy is ASSET_CLASS.
 */
export const groupFinancialProductsByAssetClassLevel = (
  translateBackendString: (translatedObject: unknown) => string,
  products: FinancialProduct[],
  assetClassLevel: AssetClassLevel | string | undefined,
  uncategorizedLabel: string,
): FinancialProductAssetClassGroup[] => {
  const field = ASSET_CLASS_FIELD_BY_LEVEL[assetClassLevel as AssetClassLevel];

  if (!field) return []; // unknown level → caller uses flat list

  const buckets = new Map<string, FinancialProduct[]>();

  for (const product of products) {
    const assetClass = product[field];
    const id =
      assetClass?.id && String(assetClass.id).trim() !== ''
        ? assetClass.id
        : UNCATEGORIZED_ASSET_CLASS_GROUP_KEY;
    const bucket = buckets.get(id);

    if (bucket) {
      bucket.push(product);
    } else {
      buckets.set(id, [product]);
    }
  }

  const headerForClass = (assetClass: AssetClass | undefined): string => {
    if (!assetClass) return uncategorizedLabel;
    const fromTranslations = translateBackendString(assetClass.translatedName);
    if (fromTranslations) return fromTranslations;
    if (assetClass.name) return assetClass.name;
    return uncategorizedLabel;
  };

  const locale = getLocaleString();
  const groups: FinancialProductAssetClassGroup[] = [];

  buckets.forEach((prods, groupKey) => {
    const first = prods[0];
    const assetClassOnProduct = first ? first[field] : undefined;
    const headerLabel =
      groupKey === UNCATEGORIZED_ASSET_CLASS_GROUP_KEY
        ? uncategorizedLabel
        : headerForClass(assetClassOnProduct);

    const sortedProducts = [...prods].sort((a, b) =>
      (a.ticker ?? '').localeCompare(b.ticker ?? '', locale),
    );
    groups.push({ groupKey, headerLabel, products: sortedProducts });
  });

  groups.sort((a, b) => {
    if (a.groupKey === UNCATEGORIZED_ASSET_CLASS_GROUP_KEY) return 1;
    if (b.groupKey === UNCATEGORIZED_ASSET_CLASS_GROUP_KEY) return -1;
    return a.headerLabel.localeCompare(b.headerLabel, locale);
  });

  return groups;
};
